import { TBuyerOfferSession } from '@pb/apps/offers/services/buyer-offer-session';
import { TStep, TStepName, TStepState } from './stepper.types';

function getStepsState(currentSession: TBuyerOfferSession | null): TStepState {
    const stepsValidityState: TStepState = {
        isMakeOfferValid: false,
        isBuyerDetailsValid: false,
        isBuyerQuestionsValid: false,
        isPurplePactValid: false,
        isSubmitOfferValid: false,
    };

    if (!currentSession) {
        return stepsValidityState;
    }

    if (isMakeOfferValid(currentSession)) {
        stepsValidityState.isMakeOfferValid = true;
    }

    if (isBuyerDetailsValid(currentSession)) {
        stepsValidityState.isBuyerDetailsValid = true;
    }

    if (isBuyerQuestionsValid(currentSession)) {
        stepsValidityState.isBuyerQuestionsValid = true;
    }

    if (isPurplePactValid(currentSession)) {
        stepsValidityState.isPurplePactValid = true;
    }

    if (isSubmitOfferValid(currentSession)) {
        stepsValidityState.isSubmitOfferValid = true;
    }

    return stepsValidityState;
}

function isMakeOfferValid(currentSession: TBuyerOfferSession): boolean {
    return currentSession.offerAmount !== 0;
}

function isBuyerDetailsValid(currentSession: TBuyerOfferSession): boolean {
    return (
        currentSession.buyers.length > 0 &&
        currentSession.buyers[0].dateOfBirth !== undefined
    );
}

function isBuyerQuestionsValid(currentSession: TBuyerOfferSession): boolean {
    return (
        currentSession.buyerAdditionalDetails.mortgageRequired !== undefined &&
        currentSession.buyerAdditionalDetails.propertyToSell !== undefined &&
        currentSession.buyerAdditionalDetails.isPbConnected !== undefined
    );
}

function isPurplePactValid(currentSession: TBuyerOfferSession): boolean {
    if (!currentSession.isPurplePactApplicable) {
        return true;
    }
    return (
        currentSession.buyerAdditionalDetails.isInterestedInPurplePact !==
        undefined
    );
}

function isSubmitOfferValid(currentSession: TBuyerOfferSession): boolean {
    return (
        isMakeOfferValid(currentSession) &&
        isBuyerDetailsValid(currentSession) &&
        isBuyerQuestionsValid(currentSession) &&
        isPurplePactValid(currentSession)
    );
}

export function getSteps(
    currentStep: TStepName,
    propertyId: number,
    currentSession: TBuyerOfferSession | null,
    isPurplePactApplicableOverride?: boolean
): TStep[] {
    const stepsValidity = getStepsState(currentSession);
    const isPurplePactApplicable = Boolean(
        currentSession?.isPurplePactApplicable || isPurplePactApplicableOverride
    );

    const defaultSteps: TStep[] = [
        {
            name: 'Offer',
            position: 'Current',
            isComplete: stepsValidity.isMakeOfferValid,
            asPath: `/sales/make-offer/${propertyId}`,
        },
        {
            name: 'Your details',
            position: 'Future',
            isComplete: stepsValidity.isBuyerDetailsValid,
            asPath: `/sales/buyer-details/${propertyId}`,
        },
        {
            name: 'Buyer info',
            position: 'Future',
            isComplete: stepsValidity.isBuyerQuestionsValid,
            asPath: `/sales/buyer-questions/${propertyId}`,
        },
        {
            name: 'Review',
            position: 'Future',
            isComplete: stepsValidity.isSubmitOfferValid,
            asPath: `/sales/submit-offer/${propertyId}`,
        },
    ];

    if (isPurplePactApplicable) {
        defaultSteps.splice(3, 0, {
            name: 'Purple Pact',
            position: 'Future',
            isComplete: stepsValidity.isPurplePactValid,
            asPath: `/sales/purple-pact/${propertyId}`,
        });
    }

    let isCurrentStepFound = false;
    const steps: TStep[] = [];
    for (const defaultStep of defaultSteps) {
        if (defaultStep.name === currentStep) {
            isCurrentStepFound = true;
            defaultStep.position = 'Current';
        } else {
            defaultStep.position = isCurrentStepFound ? 'Future' : 'Previous';
        }
        steps.push(defaultStep);
    }
    return steps;
}
